"use client";

import React, { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import Image from "next/image";

interface RaveMediaCarouselProps {
  video?: string | null;
  flyer: string;
  name: string;
}

export function RaveMediaCarousel({
  video,
  flyer,
  name,
}: RaveMediaCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="relative md:hidden">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {video && (
            <div className="flex-[0_0_100%]">
              <AspectRatio ratio={1 / 1} className="overflow-hidden">
                <video
                  src={video}
                  controls
                  playsInline
                  className="object-cover w-full h-full"
                >
                  Your browser does not support the video tag.
                </video>
              </AspectRatio>
            </div>
          )}
          <div className="flex-[0_0_100%]">
            <AspectRatio ratio={1 / 1} className="overflow-hidden">
              <Image
                src={flyer || "/placeholder.svg"}
                alt={name}
                fill
                className="object-cover"
              />
            </AspectRatio>
          </div>
        </div>
      </div>
      <div className="flex justify-center gap-[2px] mt-4">
        {scrollSnaps.map((_, index) => (
          <button
            key={index}
            className={`h-[2px] w-8 ${
              index === selectedIndex ? "bg-primary" : "bg-neutral-700"
            }`}
            onClick={() => scrollTo(index)}
          />
        ))}
      </div>
    </div>
  );
}
